/**
 * Companion Timeline Demo Data
 * VOY-12: AI Travel Companion Mockup
 */

// Types
export interface TimelineEvent {
  id: string;
  time: string;
  title: string;
  description: string;
  icon: 'car' | 'plane' | 'hotel' | 'restaurant' | 'activity' | 'info';
  status: 'upcoming' | 'now' | 'completed';
  actions: ActionButton[];
  context?: {
    traffic?: string;
    weather?: string;
    price?: string;
    location?: string;
  };
}

export interface ActionButton {
  label: string;
  variant?: 'default' | 'outline';
  onClick: () => void;
}

export interface AIInsight {
  category: 'suggestion' | 'reminder' | 'quick-action';
  icon: string;
  text: string;
  completed?: boolean;
  action?: () => void;
}

export interface ChatMessage {
  id: string;
  text: string;
  timestamp: Date;
  isUser: boolean;
}

export interface DayGroup {
  label: string;
  isToday?: boolean;
  isTomorrow?: boolean;
  events: TimelineEvent[];
}

// Demo action handlers
const handleAction = (actionName: string) => {
  console.log(`[Companion] Action clicked: ${actionName}`);
  alert(`Demo: ${actionName}\n\nIn a real app, this would execute the action.`);
};

// Timeline Events Data
const timelineEvents: TimelineEvent[] = [
  // TODAY - March 15
  {
    id: 'airport-departure',
    time: '3:00 PM',
    title: 'Airport Departure Alert',
    description: 'Time to leave for JFK Airport. Current traffic is moderate. Terminal 4, arrive by 8:30 AM for your 10:30 AM flight.',
    icon: 'car',
    status: 'now',
    context: {
      traffic: 'Moderate (35 min to JFK)',
      location: 'Terminal 4'
    },
    actions: [
      { label: 'Get Directions', onClick: () => handleAction('Get Directions') },
      { label: 'Call Uber', onClick: () => handleAction('Call Uber') }
    ]
  },
  {
    id: 'checkin-reminder',
    time: '8:30 PM',
    title: 'Check-in Reminder',
    description: 'Online check-in now open for UA 523. Seat 12A is available for selection.',
    icon: 'plane',
    status: 'upcoming',
    actions: [
      { label: 'Check In Now', onClick: () => handleAction('Check In Now') },
      { label: 'Choose Different Seat', variant: 'outline', onClick: () => handleAction('Choose Different Seat') }
    ]
  },
  {
    id: 'flight-updates',
    time: '10:30 AM',
    title: 'Flight Updates',
    description: 'UA 523 to LAX departing from Gate B42. Status: On time.',
    icon: 'plane',
    status: 'upcoming',
    context: {
      weather: '72°F sunny in LA',
      location: 'Gate B42'
    },
    actions: [
      { label: 'View Boarding Pass', onClick: () => handleAction('View Boarding Pass') },
      { label: 'Gate Map', variant: 'outline', onClick: () => handleAction('Gate Map') }
    ]
  },
  {
    id: 'arrival-assistant',
    time: '12:15 PM PT',
    title: 'Arrival Assistant',
    description: 'Landing at LAX Terminal 7. Hotel shuttle picks up at Island 3.',
    icon: 'plane',
    status: 'upcoming',
    context: {
      price: 'Uber: $35-45',
      location: 'Terminal 7, Island 3'
    },
    actions: [
      { label: 'Book Transport', onClick: () => handleAction('Book Transport') },
      { label: 'Contact Hotel', variant: 'outline', onClick: () => handleAction('Contact Hotel') }
    ]
  },
  {
    id: 'hotel-checkin',
    time: '3:00 PM PT',
    title: 'Hotel Check-in',
    description: 'Beverly Hilton check-in ready. Room 1502 prepared with your preferences (quiet room, 15th floor, city views).',
    icon: 'hotel',
    status: 'upcoming',
    context: {
      location: 'Downtown LA, 15 min from LAX'
    },
    actions: [
      { label: 'Digital Key', onClick: () => handleAction('Digital Key') },
      { label: 'Room Upgrade Options', variant: 'outline', onClick: () => handleAction('Room Upgrade Options') }
    ]
  },
  
  // TOMORROW - March 16
  {
    id: 'itinerary-start',
    time: '8:00 AM',
    title: 'Itinerary Start',
    description: 'Perfect day for outdoor activities! Getty Center opens at 10 AM.',
    icon: 'activity',
    status: 'upcoming',
    context: {
      weather: '78°F and sunny all day'
    },
    actions: [
      { label: 'Start Navigation', onClick: () => handleAction('Start Navigation') },
      { label: 'Modify Plans', variant: 'outline', onClick: () => handleAction('Modify Plans') }
    ]
  },
  {
    id: 'getty-center',
    time: '9:00 AM',
    title: 'Getty Center',
    description: 'World-renowned art collection with stunning architecture and gardens overlooking Los Angeles.',
    icon: 'activity',
    status: 'upcoming',
    context: {
      location: 'Getty Center Drive'
    },
    actions: [
      { label: 'View Tickets', onClick: () => handleAction('View Tickets') },
      { label: 'Parking Info', variant: 'outline', onClick: () => handleAction('Parking Info') }
    ]
  },
  {
    id: 'lunch-republique',
    time: '1:00 PM',
    title: 'Lunch at Republique',
    description: 'Award-winning French bistro and bakery in a stunning historic building. Reservation confirmed for 2 guests.',
    icon: 'restaurant',
    status: 'upcoming',
    context: {
      price: '$$$',
      location: '10 min from Getty'
    },
    actions: [
      { label: 'View Reservation', onClick: () => handleAction('View Reservation') },
      { label: 'Modify Time', variant: 'outline', onClick: () => handleAction('Modify Time') }
    ]
  },
  {
    id: 'griffith-observatory',
    time: '3:30 PM',
    title: 'Griffith Observatory',
    description: 'Iconic landmark with planetarium shows and breathtaking views of the city and Hollywood sign.',
    icon: 'activity',
    status: 'upcoming',
    context: {
      location: 'Griffith Park'
    },
    actions: [
      { label: 'Show Tickets (4:15 PM)', onClick: () => handleAction('Show Tickets') },
      { label: 'Parking Reserved', variant: 'outline', onClick: () => handleAction('Parking Reserved') }
    ]
  }
];

// AI Insights Data
const aiInsights: AIInsight[] = [
  // Proactive Suggestions
  {
    category: 'suggestion',
    icon: '🎯',
    text: 'Your Priority Pass works at United Club in Terminal 4'
  },
  {
    category: 'suggestion',
    icon: '🎯',
    text: 'Republique is 10 min from Getty Center, perfect for lunch timing'
  },
  {
    category: 'suggestion',
    icon: '🎯',
    text: 'Save $25 by booking LAX shuttle instead of Uber'
  },
  
  // Quick Actions
  {
    category: 'quick-action',
    icon: '⚡',
    text: 'Add TSA wait time to calendar',
    action: () => handleAction('Add TSA wait time')
  },
  {
    category: 'quick-action',
    icon: '⚡',
    text: 'Share itinerary with contacts',
    action: () => handleAction('Share itinerary')
  },
  {
    category: 'quick-action',
    icon: '⚡',
    text: 'Enable flight delay protection ($12)',
    action: () => handleAction('Enable delay protection')
  },
  {
    category: 'quick-action',
    icon: '⚡',
    text: 'Book return airport transport',
    action: () => handleAction('Book return transport')
  },
  
  // Smart Reminders
  {
    category: 'reminder',
    icon: '💡',
    text: 'Airport departure (3:00 PM)',
    completed: true
  },
  {
    category: 'reminder',
    icon: '💡',
    text: 'Online check-in (Tonight 8:30 PM)',
    completed: true
  },
  {
    category: 'reminder',
    icon: '💡',
    text: 'Prescription reminder for trip',
    completed: false
  }
];

// Chat History Data
const chatHistory: ChatMessage[] = [
  {
    id: 'msg-1',
    text: "What's the weather tomorrow?",
    timestamp: new Date('2025-03-15T14:30:00'),
    isUser: true
  },
  {
    id: 'msg-2',
    text: 'Tomorrow in Los Angeles will be 78°F and sunny all day - perfect weather for the Getty Center and Griffith Observatory visits!',
    timestamp: new Date('2025-03-15T14:30:05'),
    isUser: false
  },
  {
    id: 'msg-3',
    text: 'Find coffee near my hotel',
    timestamp: new Date('2025-03-15T15:10:00'),
    isUser: true
  },
  {
    id: 'msg-4',
    text: "Change dinner reservation to 8pm",
    timestamp: new Date('2025-03-15T15:45:00'),
    isUser: true
  }
];

// Export Functions
export function getTimelineEvents(): DayGroup[] {
  return [
    {
      label: 'TODAY - March 15',
      isToday: true,
      events: timelineEvents.slice(0, 5)
    },
    {
      label: 'TOMORROW - March 16',
      isTomorrow: true,
      events: timelineEvents.slice(5, 9)
    }
  ];
}

export function getAIInsights(): {
  suggestions: AIInsight[];
  quickActions: AIInsight[];
  reminders: AIInsight[];
} {
  return {
    suggestions: aiInsights.filter(i => i.category === 'suggestion'),
    quickActions: aiInsights.filter(i => i.category === 'quick-action'),
    reminders: aiInsights.filter(i => i.category === 'reminder')
  };
}

export function getChatHistory(): ChatMessage[] {
  return chatHistory;
}

export function getNowCardData() {
  return {
    currentTime: '2:15 PM',
    nextAction: 'Leave for airport in 45 minutes',
    context: {
      traffic: 'Moderate (35 min to JFK)',
      info: 'Terminal 4, Gate B42'
    },
    actions: [
      { label: 'Set Reminder', onClick: () => handleAction('Set Reminder') },
      { label: 'Book Uber', variant: 'outline' as const, onClick: () => handleAction('Book Uber') }
    ]
  };
}

